import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useToast } from '../context/ToastContext';
import { useTranslation } from 'react-i18next';
import { ProdRow } from '../components/OSProductivityTable';
import { RNInterProductActualRow, RNInterProductReceipt } from '../types/Types';
import { RNMachineRepair } from '../types/rnMachineRepairs';
import { useDocumentTypesContext } from '../context/DocumentTypesContext';
import { resolveRoleCode } from '../utils/workOrderRole';
import { WorkOrderRole } from '../types/Types';

export interface RNRazmerActualRow {
  rowKey: string;
  sourceRowKey?: string;
  measuredQty: string;
  serialNumber?: string;
  expiryDate?: string;
  waste?: string;
  rastur?: string;
  workedBy?: string | { _id?: string };
  workedByName?: string;
  workedAt?: string;
  verifiedBy?: string | { _id?: string };
  verifiedByName?: string;
  verifiedAt?: string;
  verifyRequested?: boolean;
  confirmed?: boolean;
}

export interface RNMaterialnoActualRow extends RNRazmerActualRow {}

export type RNRazmerDispatchStage = 'preparation' | 'packaging';

export interface RNRazmerDispatchRow {
  rowKey: string;
  item?: string;
  ident: string;
  name: string;
  unitCode: string;
  measuredQty: string;
  confirmed: boolean;
  verified: boolean;
  serialNumber?: string;
  expiryDate?: string;
  waste?: string;
  rastur?: string;
}

export interface RNRazmerDispatch {
  stage: RNRazmerDispatchStage;
  sentTo?: string | { _id?: string; firstName?: string; lastName?: string; userName?: string };
  sentToName?: string;
  sentBy?: string | { _id?: string; firstName?: string; lastName?: string; userName?: string };
  sentByName?: string;
  sentAt?: string;
  rows: RNRazmerDispatchRow[];
}

// One 6421 material-issuance row sent to POST /:id/issue-6421 — the row's identity
// (rowKey) is the Материјално row's own osRowId, same convention as razmer/materialno
// actual rows' rowKey. quantity/serial/deadline are never sent by the client (the
// server re-derives quantity, never trusts the body — see rnWorkOrder.js step 10) but
// come back populated once the document is created and the saved rnWorkOrder is
// re-fetched, so they're typed here as response-only fields. Reused verbatim as the
// request row shape for POST /:id/receive-7201 (7201's request body is identical).
export interface RNMaterialnoIssueRow {
  rowKey: string;
  item: string;
  ident: string;
  name: string;
  unitCode: string;
  quantity?: number;
  serial?: string;
  deadline?: string;
}

// One issued-6421 record per stage, as stored on RNWorkOrderDoc.materialnoIssues —
// server-populated after a successful POST /:id/issue-6421.
export interface RNMaterialnoIssue {
  // The document type's own code (e.g. '6421'), whichever document type is configured
  // for that role — never a fixed literal set. Resolve to a role via
  // utils/workOrderRole's issueEntryRole(), never compare this field to a code directly.
  docTypeCode?: string;
  // Id of the document type (survives a code rename); issueRole is server-stamped.
  documentType?: string;
  issueRole?: WorkOrderRole | "";
  subprocessId: string;
  document: string;
  documentNumber?: string;
  warehouse?: string;
  issuedBy?: string | { _id?: string };
  issuedByName?: string;
  issuedAt?: string;
  rows: RNMaterialnoIssueRow[];
}

// One InProcess / montage parameter value as recorded on the work order. `entryId` is
// the _id of the entry in the Subprocess codebook; the RN value is the operator's reading,
// separate from the Ingredient's value for the same entry, which is the specification.
export interface RNParamEntry {
  subprocessId?: string;
  entryId: string;
  value: string;
}

// RN work order (работен налог) ACTUALS document — one doc per item, referencing the
// OS Ingredient it was executed against. This hook talks ONLY to /api/rn-work-orders.
// It must NEVER be pointed at /api/ingredients — that endpoint is OS's, and RN reads
// OS live elsewhere (useRNDetail) purely as reference.
export interface RNWorkOrderDoc {
  quarantineIntermediateRowKeys?: string[];
  qcApprovedIntermediateRowKeys?: string[];
  intermediateRequiresQcRelease?: boolean;
  qcReleasedIntermediateRowKeys?: string[];
  qcRejectedIntermediateRowKeys?: string[];
  normativSnapshot?: any;
  normativVersion?: number;
  documentType?: string;
  buttonConfig?: Array<{
    docTypeCode: string;
    documentType?: string;
    direction: "issuance" | "reception" | "transfer";
    buttonScope: "stage" | "workOrder";
    productionStatus?: string | { _id: string; code?: string; name?: string } | null;
    label?: string;
    labelEnglish?: string;
  }>;
  materialTransfers?: Array<{
    subprocessId: string;
    docTypeCode?: string;
    documentType?: string;
    documents: Array<string | { _id: string; number?: string; code?: string }>;
    createdBy?: string | { _id?: string };
    createdAt?: string;
  }>;
  // Server-computed rights of the current user on this RN (UX only; the server enforces).
  viewer?: { allStages: boolean; subprocessIds: string[]; canMoveStock: boolean };
  _id: string;
  number?: string;
  documentCode?: string;
  productionDate?: string | null;
  productionPlanNumber?: string | null;
  plannedStartDate?: string | null;
  status?: string | null;
  items: string | { _id: string; ident?: string; name?: string };
  ingredient: string | { _id: string } | null;
  productivity: ProdRow[];
  razmerActuals?: RNRazmerActualRow[];
  materialnoActuals?: RNMaterialnoActualRow[];
  interProductActuals?: RNInterProductActualRow[];
  razmerDispatches?: RNRazmerDispatch[];
  materialnoIssues?: RNMaterialnoIssue[];
  interProductReceipts?: RNInterProductReceipt[];
  inProcessParams?: RNParamEntry[];
  montageParamValues?: RNParamEntry[];
  machineRepairs?: RNMachineRepair[];
  // Подготвил — server-stamped on first save, no PIN. Одобрил — PIN-signed, mirrors OS.
  prepared?: { user?: string; userName?: string; date?: string };
  approved?: { user?: string; userName?: string; date?: string; signature?: string };
  createdBy?: any;
  updatedBy?: any;
  createdAt?: string;
  updatedAt?: string;
}

interface SaveProductivityParams {
  documentCode?: string;
  status?: string | null;
  items: string;
  ingredient?: string | null;
  productivity: ProdRow[];
  razmerActuals?: RNRazmerActualRow[];
  materialnoActuals?: RNMaterialnoActualRow[];
  interProductActuals?: RNInterProductActualRow[];
  inProcessParams?: RNParamEntry[];
  montageParamValues?: RNParamEntry[];
  machineRepairs?: RNMachineRepair[];
}

export const useRNWorkOrder = () => {
  const { showToast } = useToast();
  const { t } = useTranslation();
  const { documentTypes } = useDocumentTypesContext();
  const [rnWorkOrder, setRnWorkOrderRaw] = useState<RNWorkOrderDoc | null>(null);
  // Keep the previous `viewer` when a response for the same RN does not carry one.
  const setRnWorkOrder = useCallback((next: RNWorkOrderDoc | null) => {
    setRnWorkOrderRaw((prev) => (
      next && !next.viewer && prev?.viewer && prev._id === next._id ? { ...next, viewer: prev.viewer } : next
    ));
  }, []);
  const [rnLoading, setRnLoading] = useState<boolean>(false);
  // Latest RN + types for the URL-code resolver (callbacks must stay stable).
  const rnRef = useRef<RNWorkOrderDoc | null>(null);
  rnRef.current = rnWorkOrder;
  const typesRef = useRef(documentTypes);
  typesRef.current = documentTypes;
  const loadSequenceRef = useRef(0);
  const currentItemIdRef = useRef('');

  const apiUrl = process.env.REACT_APP_API_URL ?? '';
  const baseUrl = `${apiUrl}/api/rn-work-orders`;

  // The server dispatches issue-/receive- by the CURRENT code of the type, so the URL
  // code is resolved from the role, never a literal. Unresolvable -> no call; the error
  // carries response.data.message so the caller's existing error toast shows it.
  const codeForRole = useCallback((role: WorkOrderRole): string => {
    const code = resolveRoleCode(role, typesRef.current, rnRef.current?.buttonConfig);
    if (!code) {
      const message = t('documentTypeConfig.roleDocumentUnresolved');
      throw Object.assign(new Error(message), { response: { data: { message } } });
    }
    return encodeURIComponent(code);
  }, [t]);

  const clearRN = useCallback(() => {
    loadSequenceRef.current += 1;
    currentItemIdRef.current = '';
    setRnWorkOrder(null);
  }, []);

  // GET /by-item/:itemId — 200 with the doc or null (null = no RN yet).
  const loadRNByItem = useCallback(async (itemId: string, documentCode?: string): Promise<RNWorkOrderDoc | null> => {
    if (!itemId) {
      clearRN();
      return null;
    }
    const sequence = ++loadSequenceRef.current;
    currentItemIdRef.current = itemId;
    setRnWorkOrder(null);
    setRnLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axios.get<RNWorkOrderDoc | null>(`${baseUrl}/by-item/${itemId}`, {
        headers: { 'x-auth-token': token },
        params: documentCode ? { documentCode } : undefined,
      });

      if (sequence === loadSequenceRef.current && currentItemIdRef.current === itemId) {
        setRnWorkOrder(response.data || null);
      }
      return response.data || null;
    } catch (err: any) {
      if (sequence === loadSequenceRef.current && currentItemIdRef.current === itemId) setRnWorkOrder(null);
      return null;
    } finally {
      if (sequence === loadSequenceRef.current) setRnLoading(false);
    }
  }, [baseUrl, clearRN]);

  // GET /:id — loads ONE specific RN by its own id, not "the RN of this item". A
  // product now has MANY RNs (one per batch); the toolbar search / navigation picks a
  // specific row, and this is how the screen loads exactly that one instead of
  // silently falling back to loadRNByItem's latest-by-item.
  const loadRNById = useCallback(async (id: string): Promise<RNWorkOrderDoc | null> => {
    if (!id) {
      clearRN();
      return null;
    }
    const sequence = ++loadSequenceRef.current;
    setRnWorkOrder(null);
    setRnLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axios.get<RNWorkOrderDoc>(`${baseUrl}/${id}`, {
        headers: { 'x-auth-token': token },
      });

      const doc = response.data || null;
      if (sequence === loadSequenceRef.current) {
        const itemId = doc ? (typeof doc.items === 'string' ? doc.items : doc.items?._id) : '';
        currentItemIdRef.current = itemId || '';
        setRnWorkOrder(doc);
      }
      return doc;
    } catch (err: any) {
      if (sequence === loadSequenceRef.current) setRnWorkOrder(null);
      return null;
    } finally {
      if (sequence === loadSequenceRef.current) setRnLoading(false);
    }
  }, [baseUrl, clearRN]);

  // Creates the RN doc on first save (POST), then updates it (PUT) once it belongs to
  // the same ident (currentItemIdRef, kept in sync by loadRNByItem/loadRNById/the POST
  // branch below). ONE RN = ONE BATCH — a product has many RNs over time, so a fresh
  // РН (isCreating on the screen) must POST even when an older RN of the same ident is
  // already the latest, never PUT into it.
  const saveProductivity = useCallback(async (params: SaveProductivityParams): Promise<RNWorkOrderDoc | null> => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');

    try {
      if (rnWorkOrder?._id && currentItemIdRef.current === params.items) {
        const response = await axios.put(
          `${baseUrl}/${rnWorkOrder._id}`,
          {
            documentCode: params.documentCode,
            status: params.status ?? null,
            ingredient: params.ingredient ?? null,
            productivity: params.productivity,
            razmerActuals: params.razmerActuals ?? [],
            materialnoActuals: params.materialnoActuals ?? [],
            interProductActuals: params.interProductActuals ?? [],
            inProcessParams: params.inProcessParams ?? [],
            montageParamValues: params.montageParamValues ?? [],
            machineRepairs: params.machineRepairs ?? [],
          },
          { headers: { 'x-auth-token': token, 'Content-Type': 'application/json' } }
        );
        const saved = response.data.rnWorkOrder || null;
        setRnWorkOrder(saved);
        return saved;
      } else {
        const response = await axios.post(
          baseUrl,
          {
            documentCode: params.documentCode,
            status: params.status ?? null,
            items: params.items,
            ingredient: params.ingredient ?? null,
            productivity: params.productivity,
            razmerActuals: params.razmerActuals ?? [],
            materialnoActuals: params.materialnoActuals ?? [],
            interProductActuals: params.interProductActuals ?? [],
            inProcessParams: params.inProcessParams ?? [],
            montageParamValues: params.montageParamValues ?? [],
            machineRepairs: params.machineRepairs ?? [],
          },
          { headers: { 'x-auth-token': token, 'Content-Type': 'application/json' } }
        );
        const saved = response.data.rnWorkOrder || null;
        // isCreating cleared currentItemIdRef (via clearRN) before this POST, so it must
        // be set here — otherwise the very next save on this same new RN would see it
        // stay '', fail the PUT check above, and POST a second RN for one save.
        currentItemIdRef.current = params.items;
        setRnWorkOrder(saved);
        return saved;
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to save RN work order';
      const messageKey = err.response?.data?.messageKey;
      showToast(messageKey ? t(messageKey) : message, 'error');
      throw err;
    }
  }, [baseUrl, rnWorkOrder, showToast, t]);

  // PIN-signs Одобрил on the current RN doc. Backend requires Подготвил to already
  // exist (400 otherwise) — the UI also gates the button on preparedUserName.
  const signRN = useCallback(async (pin: string): Promise<RNWorkOrderDoc | null> => {
    if (!rnWorkOrder?._id) return null;
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');

    const response = await axios.post(
      `${baseUrl}/${rnWorkOrder._id}/sign/approved`,
      { field: 'approved', pin },
      { headers: { 'x-auth-token': token, 'Content-Type': 'application/json' } }
    );
    const saved = response.data.rnWorkOrder || null;
    setRnWorkOrder(saved);
    return saved;
  }, [baseUrl, rnWorkOrder]);

  // Removes only the Одобрил signature; Подготвил (and its own admin-only reset) is untouched.
  const removeApprovedSignature = useCallback(async (): Promise<void> => {
    if (!rnWorkOrder?._id) return;
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');

    try {
      const response = await axios.delete(`${baseUrl}/${rnWorkOrder._id}/sign/approved`, {
        headers: { 'x-auth-token': token },
      });
      setRnWorkOrder(response.data.rnWorkOrder || null);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to remove signature';
      showToast(message, 'error');
      throw err;
    }
  }, [baseUrl, rnWorkOrder, showToast]);

  const dispatchRazmerStage = useCallback(async (
    stage: RNRazmerDispatchStage,
    sentTo: string,
    rows: RNRazmerDispatchRow[]
  ): Promise<RNWorkOrderDoc | null> => {
    if (!rnWorkOrder?._id) return null;
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');

    try {
      const response = await axios.post(
        `${baseUrl}/${rnWorkOrder._id}/razmer-dispatches`,
        { stage, sentTo, rows },
        { headers: { 'x-auth-token': token, 'Content-Type': 'application/json' } }
      );
      const saved = response.data.rnWorkOrder || null;
      setRnWorkOrder(saved);
      return saved;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to send razmer rows';
      showToast(message, 'error');
      throw err;
    }
  }, [baseUrl, rnWorkOrder, showToast]);

  // POST /:id/issue-6421 — consumes the stage's confirmed Материјално rows from stock as
  // a 6421 issuance document. One 6421 per stage; a second call for the same subprocess
  // gets a 409 from the backend. Errors are intentionally NOT caught/toasted here — the
  // 409 (messageKey + existingId/existingNumber) and 400 (stockErrors) response bodies
  // carry detail only the caller can render into a useful toast.
  const issueMaterial6421 = useCallback(async (
    rnId: string,
    subprocessId: string,
    rows: RNMaterialnoIssueRow[]
  ): Promise<{ message?: string; document?: { _id: string; number?: string }; rnWorkOrder: RNWorkOrderDoc | null }> => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');

    const response = await axios.post(
      `${baseUrl}/${rnId}/issue-${codeForRole('issue-materials')}`,
      { subprocessId, rows },
      { headers: { 'x-auth-token': token, 'Content-Type': 'application/json' } }
    );
    const saved = response.data.rnWorkOrder || null;
    setRnWorkOrder(saved);
    return { message: response.data.message, document: response.data.document, rnWorkOrder: saved };
  }, [baseUrl, codeForRole]);

  const issueMaterial6421Razmer = useCallback(async (
    rnId: string,
    rows: RNMaterialnoIssueRow[]
  ): Promise<{ message?: string; document?: { _id: string; number?: string }; rnWorkOrder: RNWorkOrderDoc | null }> => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');

    const response = await axios.post(
      `${baseUrl}/${rnId}/issue-6421-razmer`,
      { rows },
      { headers: { 'x-auth-token': token, 'Content-Type': 'application/json' } }
    );
    const saved = response.data.rnWorkOrder || null;
    setRnWorkOrder(saved);
    return { message: response.data.message, document: response.data.document, rnWorkOrder: saved };
  }, [baseUrl]);

  // POST /:id/issue-6425-razmer — issues the recipe's finished-product rows (accounting
  // type 3xx) as a 6425 document. Mirrors issueMaterial6421Razmer, but the body is empty:
  // the server derives the finished-product rows itself (it knows the accounting type,
  // the client's cached copy may not — Rule 6 partial-item-cache). Errors are
  // intentionally NOT caught/toasted here — the caller renders the 409/400 response body.
  const issueMaterial6425Razmer = useCallback(async (
    rnId: string
  ): Promise<{ message?: string; document?: { _id: string; number?: string }; rnWorkOrder: RNWorkOrderDoc | null }> => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');

    const response = await axios.post(
      `${baseUrl}/${rnId}/issue-6425-razmer`,
      {},
      { headers: { 'x-auth-token': token, 'Content-Type': 'application/json' } }
    );
    const saved = response.data.rnWorkOrder || null;
    setRnWorkOrder(saved);
    return { message: response.data.message, document: response.data.document, rnWorkOrder: saved };
  }, [baseUrl]);

  // POST /:id/issue-6423 — issues the stage's confirmed Размерување rastur (waste/scrap)
  // quantities as a 6423 Растур material document. Mirrors issueMaterial6421 exactly: one
  // 6423 per stage, a second call for the same subprocess gets a 409, quantity is never
  // sent (the server re-derives it from razmerActuals[].rastur), and errors are
  // intentionally NOT caught/toasted here — the caller renders the 409/400 response body.
  const issueRastur6423 = useCallback(async (
    rnId: string,
    subprocessId: string,
    rows: RNMaterialnoIssueRow[]
  ): Promise<{ message?: string; document?: { _id: string; number?: string }; rnWorkOrder: RNWorkOrderDoc | null }> => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');

    const response = await axios.post(
      `${baseUrl}/${rnId}/issue-${codeForRole('waste')}`,
      { subprocessId, rows },
      { headers: { 'x-auth-token': token, 'Content-Type': 'application/json' } }
    );
    const saved = response.data.rnWorkOrder || null;
    setRnWorkOrder(saved);
    return { message: response.data.message, document: response.data.document, rnWorkOrder: saved };
  }, [baseUrl, codeForRole]);

  // POST /:id/receive-7201 — receives the stage's confirmed interProductActuals as a
  // 7201 receipt document (stock IN, no FEFO, no assertSufficientStock — a receipt
  // creates a lot, it does not consume one). One 7201 per stage; a second call for the
  // same subprocess gets a 409 ({ message: 'За оваа фаза веќе е генериран документ
  // 7201.' }). Mirrors issueMaterial6421 exactly — errors are intentionally NOT
  // caught/toasted here, the caller renders the 409/400 response body into a toast.
  const receiveMaterial7201 = useCallback(async (
    rnId: string,
    subprocessId: string,
    rows: RNMaterialnoIssueRow[]
  ): Promise<{ message?: string; document?: { _id: string; number?: string }; rnWorkOrder: RNWorkOrderDoc | null }> => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');

    const response = await axios.post(
      `${baseUrl}/${rnId}/receive-${codeForRole('receive-intermediate')}`,
      { subprocessId, rows },
      { headers: { 'x-auth-token': token, 'Content-Type': 'application/json' } }
    );
    const saved = response.data.rnWorkOrder || null;
    setRnWorkOrder(saved);
    return { message: response.data.message, document: response.data.document, rnWorkOrder: saved };
  }, [baseUrl, codeForRole]);

  const createMaterialDocument = useCallback(async (
    rnId: string,
    subprocessId: string,
    endpoint: string,
    rows?: RNMaterialnoIssueRow[]
  ): Promise<{ document?: { _id: string; number?: string }; rnWorkOrder: RNWorkOrderDoc | null }> => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');
    const response = await axios.post(`${baseUrl}/${rnId}/${endpoint}`, { subprocessId, rows }, {
      headers: { 'x-auth-token': token, 'Content-Type': 'application/json' },
    });
    const saved = response.data.rnWorkOrder || null;
    setRnWorkOrder(saved);
    return { document: response.data.document, rnWorkOrder: saved };
  }, [baseUrl]);

  const issueMaterial6422 = useCallback((rnId: string, subprocessId: string, docTypeCode: string, rows: RNMaterialnoIssueRow[]) =>
    createMaterialDocument(rnId, subprocessId, `issue-${encodeURIComponent(docTypeCode)}`, rows), [createMaterialDocument]);

  // POST /:id/issue-6424 — sampling is a work-order-level issuance. The server
  // derives its own rows, so no stage or client-provided quantities are accepted.
  const issueSampling6424 = useCallback(async (rnId: string): Promise<{ document?: { _id: string; number?: string }; rnWorkOrder: RNWorkOrderDoc | null }> => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');
    const response = await axios.post(`${baseUrl}/${rnId}/issue-${codeForRole('sampling')}`, {}, {
      headers: { 'x-auth-token': token, 'Content-Type': 'application/json' },
    });
    const saved = response.data.rnWorkOrder || null;
    setRnWorkOrder(saved);
    return { document: response.data.document, rnWorkOrder: saved };
  }, [baseUrl, codeForRole]);

  const receiveFinishedProduct7200 = useCallback((rnId: string, subprocessId: string) =>
    createMaterialDocument(rnId, subprocessId, `receive-${codeForRole('receive-finished')}`), [createMaterialDocument, codeForRole]);

  const receiveFinishedProduct7200Prost = useCallback(async (
    rnId: string
  ): Promise<{ message?: string; document?: { _id: string; number?: string }; rnWorkOrder: RNWorkOrderDoc | null }> => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');

    const response = await axios.post(
      `${baseUrl}/${rnId}/receive-7200-prost`,
      {},
      { headers: { 'x-auth-token': token, 'Content-Type': 'application/json' } }
    );
    const saved = response.data.rnWorkOrder || null;
    setRnWorkOrder(saved);
    return { message: response.data.message, document: response.data.document, rnWorkOrder: saved };
  }, [baseUrl]);

  const transferMaterial = useCallback(async (rnId: string, subprocessId: string) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');
    const response = await axios.post<{ documents: Array<{ _id: string; number: string; code: string }>; rnWorkOrder: RNWorkOrderDoc }>(
      `${baseUrl}/${rnId}/transfer`, { subprocessId }, { headers: { 'x-auth-token': token } }
    );
    setRnWorkOrder(response.data.rnWorkOrder);
    return response.data;
  }, [baseUrl]);

  const issueConfiguredWasteDocument = useCallback(async (
    rnId: string,
    docTypeCode: string,
    subprocessId: string,
    source: 'razmer' | 'materialno'
  ): Promise<{ document?: { _id: string; number?: string }; rnWorkOrder: RNWorkOrderDoc | null }> => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');
    const response = await axios.post(`${baseUrl}/${rnId}/issue-${docTypeCode}`, { subprocessId, source }, {
      headers: { 'x-auth-token': token, 'Content-Type': 'application/json' },
    });
    const saved = response.data.rnWorkOrder || null;
    setRnWorkOrder(saved);
    return { document: response.data.document, rnWorkOrder: saved };
  }, [baseUrl]);

  const transferConfiguredScrapDocument = useCallback(async (
    rnId: string,
    docTypeCode: string,
    subprocessId: string,
    source: 'razmer' | 'materialno'
  ): Promise<{ document?: { _id: string; number?: string }; documents?: Array<{ _id: string; number?: string }>; rnWorkOrder: RNWorkOrderDoc | null }> => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');
    const response = await axios.post(`${baseUrl}/${rnId}/transfer-${docTypeCode}`, { subprocessId, source }, {
      headers: { 'x-auth-token': token, 'Content-Type': 'application/json' },
    });
    const saved = response.data.rnWorkOrder || null;
    setRnWorkOrder(saved);
    return { document: response.data.document, documents: response.data.documents, rnWorkOrder: saved };
  }, [baseUrl]);

  // Admin-only — clears both Подготвил and Одобрил (backend enforces the role check too).
  const resetPrepared = useCallback(async (): Promise<void> => {
    if (!rnWorkOrder?._id) return;
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');

    try {
      const response = await axios.delete(`${baseUrl}/${rnWorkOrder._id}/prepared`, {
        headers: { 'x-auth-token': token },
      });
      setRnWorkOrder(response.data.rnWorkOrder || null);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to reset prepared';
      showToast(message, 'error');
      throw err;
    }
  }, [baseUrl, rnWorkOrder, showToast]);

  const deleteRN = useCallback(async (id?: string): Promise<void> => {
    const rnId = id || rnWorkOrder?._id;
    if (!rnId) return;

    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');

    try {
      await axios.delete(`${baseUrl}/${rnId}`, {
        headers: { 'x-auth-token': token },
      });
      setRnWorkOrder(null);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to delete RN work order';
      showToast(message, 'error');
      throw err;
    }
  }, [baseUrl, rnWorkOrder, showToast]);

  // PATCH /:id/machine-repairs/approval — the GMP signature writes on its own, not via
  // saveProductivity. approvedAt must be the moment of the click; rolled into the big
  // save it would record whenever Зачувај happened to be pressed, and an unsaved
  // signature would not exist at all. Errors are left to the caller to toast.
  const approveMachineRepair = useCallback(async (
    rnId: string,
    subprocessId: string,
    machineId: string,
    approved: boolean
  ): Promise<RNWorkOrderDoc> => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');
    const response = await axios.patch<{ rnWorkOrder: RNWorkOrderDoc }>(
      baseUrl + '/' + rnId + '/machine-repairs/approval',
      { subprocessId, machineId, approved },
      { headers: { 'x-auth-token': token } }
    );
    setRnWorkOrder(response.data.rnWorkOrder);
    return response.data.rnWorkOrder;
  }, [baseUrl]);

  return {
    rnWorkOrder,
    rnLoading,
    loadRNByItem,
    loadRNById,
    saveProductivity,
    signRN,
    removeApprovedSignature,
    dispatchRazmerStage,
    issueMaterial6421,
    issueMaterial6421Razmer,
    issueMaterial6425Razmer,
    issueRastur6423,
    issueMaterial6422,
    issueSampling6424,
    receiveMaterial7201,
    receiveFinishedProduct7200,
    receiveFinishedProduct7200Prost,
    transferMaterial,
    issueConfiguredWasteDocument,
    transferConfiguredScrapDocument,
    resetPrepared,
    deleteRN,
    approveMachineRepair,
    clearRN,
  };
};
